/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { createContext, useContext, useState, ReactNode } from'react';
import { useMediaQuery } from'@/shared/hooks/use-media-query';
import { useTranslation } from'react-i18next';
import { CopyIcon, CheckIcon } from'@radix-ui/react-icons';
import { cn } from'@/shared/ui/utils/tailwind';
import { Divider } from'@/shared/ui/components/basic/Divider';
import { Drawer } from'@/shared/ui/components/basic/Drawer';
import { Tooltip } from'@/shared/ui/components/basic/Tooltip';

interface MobileErrorDrawerState {
  open: boolean;
  message: string;
  technicalDetails: string | null;
  title: string;
}

interface ToastContextType {
  mobileErrorDrawer: MobileErrorDrawerState;
  setMobileErrorDrawer: (state: MobileErrorDrawerState) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

// Fallback state for when ToastProvider is not available
const fallbackState: MobileErrorDrawerState = {
  open: false,
  message:'',
  technicalDetails: null,
  title:'',
};

/**
 * Fallback function that warns when ToastProvider is not available.
 * 
 * Used as a fallback when useToastContext is called outside of ToastProvider.
 * Logs a warning to help developers identify missing provider setup.
 * 
 * @returns {void}
 */
const fallbackSetState = () => {
  console.warn('ToastProvider not found. Mobile drawer will not work. Please wrap your app with ToastProvider.');
};

/**
 * Hook to access Toast context for mobile error drawer functionality.
 * 
 * Returns the mobile error drawer state and setter function.
 * If ToastProvider is not available, returns fallback values that log warnings.
 * 
 * @returns {ToastContextType} Object containing mobileErrorDrawer state and setMobileErrorDrawer function
 * 
 * @example
 *```tsx
 * const { mobileErrorDrawer, setMobileErrorDrawer } = useToastContext();
 * 
 * // Open drawer with error details
 * setMobileErrorDrawer({
 * open: true,
 * message:'Error occurred',
 * technicalDetails:'Stack trace...',
 * title:'Error'
 * });
 *```
 */
export const useToastContext = () => {
  const context = useContext(ToastContext);
  // Return fallback if context is not available (for backwards compatibility)
  if (!context) {
    return {
      mobileErrorDrawer: fallbackState,
      setMobileErrorDrawer: fallbackSetState,
    };
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * ToastProvider component.
 * 
 * Provides global toast context and renders mobile error drawer.
 * Must be rendered once at the app level (typically in UIProvider or MainProvider).
 * 
 * @param {ToastProviderProps} props - Component props
 * @returns {JSX.Element} Provider with mobile drawer
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const { t } = useTranslation('common');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [mobileErrorDrawer, setMobileErrorDrawer] = useState<MobileErrorDrawerState>({
    open: false,
    message:'',
    technicalDetails: null,
    title:'',
  });
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (mobileErrorDrawer.technicalDetails && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(mobileErrorDrawer.technicalDetails);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <ToastContext.Provider value={{ mobileErrorDrawer, setMobileErrorDrawer }}>
      {children}
      {isMobile && (
        <Drawer
          opened={mobileErrorDrawer.open}
          onClose={() => setMobileErrorDrawer({ ...mobileErrorDrawer, open: false })}
          position="bottom"
          title={mobileErrorDrawer.title}
          size="100%"
        >
          <div className="flex flex-col gap-4">
            <div className="text-sm text-[var(--text-primary)] leading-relaxed">
              {mobileErrorDrawer.message}
            </div>
            
            {mobileErrorDrawer.technicalDetails && (
              <>
                <Divider />
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium text-[var(--text-secondary)]">
                    {t('messages.error.technicalDetails', { defaultValue:'Technical Details' })}
                  </div>
                  <Tooltip label={copied ? t('messages.error.copied', { defaultValue:'Copied!' }) : t('messages.error.copyDetails', { defaultValue:'Copy technical details' })}>
                    <button
                      onClick={handleCopy}
                      data-testid="copy-button"
                      className={cn(
                        "flex items-center justify-center min-w-[44px] min-h-[44px]",
                        "rounded-(--radius-md) bg-transparent hover:bg-[var(--surface-hover)]",
                        "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                        "transition-colors"
                      )}
                      aria-label={t('messages.error.copyDetails', { defaultValue:'Copy technical details' })}
                    >
                      {copied ? <CheckIcon width={18} height={18} /> : <CopyIcon width={18} height={18} />}
                    </button>
                  </Tooltip>
                </div>
                <div className={cn(
                  "bg-[var(--surface-secondary)] rounded-(--radius-lg) p-4",
                  "border border-[var(--border-subtle)]",
                  "max-h-[calc(100vh-300px)] overflow-y-auto overflow-x-auto",
                  ""
                )}>
                  <pre className={cn(
                    "font-mono text-xs leading-normal text-[var(--text-primary)]",
                    "m-0 whitespace-pre-wrap break-words",
                    ""
                  )}>
                    {mobileErrorDrawer.technicalDetails}
                  </pre>
                </div>
              </>
            )}
          </div>
        </Drawer>
      )}
    </ToastContext.Provider>
  );
};
